// /rewards: the connected wallet is the account (no sign-in). Shows GET /api/rewards/:address for it:
// shiller rewards (Claim: the server pays everything settled from the coins' pools, no signature needed),
// posts waiting for their payout, per-coin earnings, the posts, the X account linked by the wallet's first post,
// and creator fees (pump.fun's fee sharing sends the creator's 70% automatically).
import { api } from "../lib/api.js";
import { config, epochName, explorerTx, xProfileUrl } from "../lib/config.js";
import { esc, formatSol, handle, pct, plural, shortAddr, timeAgo, toBig, toMs } from "../lib/format.js";
import { boot } from "../lib/page.js";
import { toast } from "../lib/toast.js";
import { $, avatar, note, setBusy } from "../lib/ui.js";
import { connect, friendlyError, hasWallet, NO_WALLET_MESSAGE, onWallet, state as wallet } from "../lib/wallet.js";

const S = {
  address: null,
  data: null,
  loading: false,
  error: null,
  seq: 0,
  busy: null, // "creator" | "shill"
  tx: { creator: null, shill: null }, // { status, hash } while sending
  msg: { creator: null, shill: null }, // { kind, html } after
};

const account = () => wallet.address || null;

/* ---------- wallet card ---------- */

function renderWallet() {
  const sub = $("#walletSub"), body = $("#walletBody");
  if (!hasWallet()) {
    sub.textContent = "No wallet found";
    body.innerHTML = `<p class="help">${esc(NO_WALLET_MESSAGE)}</p>`;
    return;
  }
  if (!wallet.address) {
    sub.textContent = "Not connected";
    body.innerHTML = `<p class="help">Connect a Solana wallet to see your rewards. No sign-up and no signatures: your wallet is your account.</p><div><button type="button" class="btn primary sm" id="wConnect" data-testid="rewards-connect">Connect wallet</button></div>`;
    $("#wConnect").addEventListener("click", (e) => run(e.currentTarget, async () => { await connect(); }));
    return;
  }
  sub.textContent = "Connected";
  body.innerHTML = `
    <div class="who"><span class="avatar" aria-hidden="true">${esc(wallet.address.slice(0, 1))}</span><div><div class="name mono" data-testid="rewards-address" title="${esc(wallet.address)}">${esc(shortAddr(wallet.address, 6, 6))}</div><div class="sub">Solana</div></div></div>
    <p class="help">Rewards are paid to this wallet.</p>`;
}

async function run(btn, fn) {
  setBusy(btn, true);
  try { await fn(); } catch (e) { toast(friendlyError(e), 5000); } finally { if (btn.isConnected) setBusy(btn, false); }
}

/* ---------- your X ---------- */

function renderX() {
  const sub = $("#xSub"), body = $("#xBody");
  if (!wallet.address) {
    sub.textContent = "Linked by your first post.";
    body.innerHTML = '<p class="help">Connect your wallet to see the X account linked to it.</p>';
    return;
  }
  if (!S.data) {
    sub.textContent = "Linked by your first post.";
    body.innerHTML = S.error ? '<p class="help">Couldn\'t load it right now.</p>' : '<p class="help"><span class="spin" aria-hidden="true"></span> Loading…</p>';
    return;
  }
  const x = S.data.x && S.data.x.handle ? S.data.x : null;
  const code = typeof S.data.code === "string" && S.data.code ? S.data.code : "";
  const codeLine = code ? `<p class="help">Your code is <span class="mono" style="white-space:nowrap" data-testid="rewards-code">${esc(code)}</span>. Post on X on a coin's page adds it for you. One X account belongs to one wallet; posting another wallet's code moves it there.</p>` : "";
  if (x) {
    sub.textContent = "Linked";
    body.innerHTML = `
      <div class="who">${avatar(x.handle, x.avatar)}<div><a class="name" href="${esc(xProfileUrl(x.handle))}" target="_blank" rel="noopener noreferrer" data-testid="x-handle">${esc(handle(x.handle))}</a><div class="sub" data-testid="rewards-x-status">Linked by your first post.</div></div></div>
      ${codeLine}`;
  } else {
    sub.textContent = "Not linked yet";
    body.innerHTML = `<p class="help" data-testid="rewards-x-status">Not linked yet. Your first Post on X links it.</p>${codeLine}`;
  }
}

/* ---------- earnings ---------- */

function skeletonCards() {
  const c = '<div class="card panel earn-card" aria-hidden="true"><span class="skel" style="width:40%;height:18px"></span><span class="skel" style="width:70%;height:38px"></span><span class="skel" style="height:40px"></span></div>';
  return `<div class="earn-cards">${c}${c}</div>`;
}

function emptyState(title, body, action = "", testid = "rewards-empty") {
  return `<div class="empty" style="margin-top:20px" data-testid="${testid}"><h3>${esc(title)}</h3><p>${esc(body)}</p>${action}</div>`;
}

function txLine(kind) {
  const t = S.tx[kind];
  const spin = '<span class="spin" aria-hidden="true"></span>';
  if (t && t.status === "checking") return note("", `${spin}<span>Checking…</span>`);
  if (t && t.status === "wallet") return note("", `${spin}<span>Confirm the claim in your wallet.</span>`);
  if (t && t.status === "pending") return note("", `${spin}<span>Sending your SOL…</span>`);
  const m = S.msg[kind];
  return m ? note(m.kind, m.html) : "";
}

const coinCell = (token, symbol) => {
  const sym = symbol ? `$${String(symbol).replace(/^\$/, "")}` : token ? shortAddr(token) : "—";
  return token ? `<a class="cell-coin" href="/coin/${esc(token)}">${esc(sym)}</a>` : esc(sym);
};

function perCoinTable(rows) {
  if (!rows.length) {
    return emptyState("No earnings yet", `Pick a coin, press Post on X on its page and paste the link. Your share of each ${epochName()} shows here after its payout.`, '<a class="btn ghost sm" href="/explore">Explore coins</a>', "rewards-percoin-empty");
  }
  const body = rows.map((r) => `<tr data-testid="rewards-percoin-row">
      <td>${coinCell(r.token, r.symbol)}</td>
      <td class="num">${esc(String(Number(r.posts) || 0))}</td>
      <td class="num">${esc(formatSol(r.earnedLamports))} SOL</td>
    </tr>`).join("");
  return `<div class="table-wrap panel"><table class="table" data-testid="rewards-percoin"><thead><tr><th scope="col">Coin</th><th scope="col" class="num">Counted posts</th><th scope="col" class="num">Earned</th></tr></thead><tbody>${body}</tbody></table></div>`;
}

function postsTable(posts) {
  if (!posts.length) {
    return emptyState("No posts yet", "Pick a coin, press Post on X on its page, then paste the link to your post there. It shows up here.", '<a class="btn ghost sm" href="/explore">Explore coins</a>', "rewards-posts-empty");
  }
  const rows = posts.map((p) => {
    const token = p.token || p.coin;
    const rejected = p.status === "rejected";
    const status = rejected ? '<span class="badge err">Not counted</span>' : p.status && p.status !== "verified" ? `<span class="badge pending">${esc(p.status)}</span>` : '<span class="badge ok">Verified</span>';
    const settled = p.shareLamports !== null && p.shareLamports !== undefined;
    const share = rejected ? "—" : settled ? `${esc(formatSol(p.shareLamports))} SOL` : '<span class="muted">After the payout</span>';
    return `<tr data-testid="rewards-post-row">
      <td>${coinCell(token, p.symbol)}</td>
      <td>${p.url ? `<a class="link" href="${esc(p.url)}" target="_blank" rel="noopener noreferrer">View post</a>` : "—"}</td>
      <td class="muted">${esc(timeAgo(p.createdAt ?? p.postedAt ?? p.verifiedAt) || "—")}</td>
      <td>${status}${rejected && p.reason ? `<div class="help" style="margin-top:4px">${esc(p.reason)}</div>` : ""}</td>
      <td class="num">${share}</td>
    </tr>`;
  }).join("");
  return `<div class="table-wrap panel"><table class="table" data-testid="rewards-posts"><thead><tr><th scope="col">Coin</th><th scope="col">Post</th><th scope="col">Posted</th><th scope="col">Status</th><th scope="col" class="num">Share</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

function renderEarnings() {
  const box = $("#earnings");
  const addr = account();
  if (!addr) {
    box.innerHTML = emptyState("Connect your wallet to see your rewards", "Shiller rewards from your verified posts and creator fees from coins you launched show up here.", hasWallet() ? '<button type="button" class="btn primary sm" id="eConnect">Connect wallet</button>' : "", "rewards-signedout");
    $("#eConnect")?.addEventListener("click", (e) => run(e.currentTarget, async () => { await connect(); }));
    return;
  }
  if (S.error) {
    box.innerHTML = emptyState("Couldn't load your rewards", S.error.message || "The server didn't answer. Try again in a moment.", '<button type="button" class="btn ghost sm" id="eRetry" data-testid="rewards-retry">Try again</button>', "rewards-error");
    $("#eRetry").addEventListener("click", () => loadRewards(true));
    return;
  }
  if (!S.data) { box.innerHTML = skeletonCards(); return; }

  const cfg = config();
  const d = S.data;
  const owed = toBig(d.creatorOwedLamports);
  const paid = toBig(d.creatorPaidLamports);
  const shill = d.shill || {};
  const claimable = toBig(shill.claimableLamports);
  const earned = toBig(shill.earnedLamports);
  const waiting = Number(shill.waitingPosts || 0);
  const canShill = claimable > 0n;
  const perCoin = Array.isArray(d.perCoin) ? d.perCoin.filter((r) => r && r.token).sort((a, b) => (toBig(b.earnedLamports) > toBig(a.earnedLamports) ? 1 : toBig(b.earnedLamports) < toBig(a.earnedLamports) ? -1 : 0)) : [];
  const posts = Array.isArray(d.posts) ? d.posts.filter(Boolean).sort((a, b) => (toMs(b.createdAt ?? b.postedAt) || 0) - (toMs(a.createdAt ?? a.postedAt) || 0)) : [];
  const creatorPct = pct(cfg.split.creatorBps);

  box.innerHTML = `
    <div class="earn-cards">
      <section class="card panel earn-card" aria-labelledby="shill-title" data-testid="shill-card">
        <div class="card-head" style="margin:0"><h2 class="card-title" id="shill-title">Shiller rewards</h2><p class="card-sub">From your verified posts. Payouts run every ${esc(String(cfg.epochMinutes))} minutes for each coin.</p></div>
        <div class="big-num"><span data-testid="shill-claimable">${esc(formatSol(claimable))}</span><small>SOL to claim</small></div>
        <div class="kv">
          <div><span>Earned in total</span><span data-testid="shill-cumulative">${esc(formatSol(earned))} SOL</span></div>
          <div><span>Already claimed</span><span data-testid="shill-claimed">${esc(formatSol(shill.claimedLamports))} SOL</span></div>
          <div><span>Waiting for their payout</span><span data-testid="shill-pending">${esc(plural(waiting, "post"))}</span></div>
        </div>
        ${waiting > 0 ? `<p class="help" data-testid="shill-pending-help">Their share shows up when their ${esc(epochName(cfg))} ends.</p>` : ""}
        <div><button type="button" class="btn primary" id="claimShill" data-testid="claim-shill" ${canShill && S.busy === null ? "" : "disabled"}>${canShill ? "Claim" : "Nothing to claim"}</button></div>
        ${earned > 0n ? "" : `<p class="help">Your share of each ${esc(epochName(cfg))} shows here after its payout. Claiming is free: Shilled pays the network fee, and there's no minimum.</p>`}
        <div data-testid="claim-shill-status">${txLine("shill")}</div>
      </section>
      <section class="card panel earn-card" aria-labelledby="creator-title" data-testid="creator-card">
        <div class="card-head" style="margin:0"><h2 class="card-title" id="creator-title">Creator fees</h2><p class="card-sub" data-testid="creator-auto">Your ${esc(creatorPct)} of creator fees is sent to your wallet automatically every ${esc(String(cfg.harvestMinutes))} minutes.</p></div>
        <div class="big-num"><span data-testid="creator-paid">${esc(formatSol(paid))}</span><small>SOL received</small></div>
        ${owed > 0n ? `
        <div class="kv"><div><span>Waiting to be claimed</span><span><span data-testid="creator-owed">${esc(formatSol(owed))}</span> SOL</span></div></div>
        <p class="help" data-testid="creator-owed-help">Waiting for the next automatic payment.</p>
        <div><button type="button" class="btn primary" id="claimCreator" data-testid="claim-creator" ${S.busy === null ? "" : "disabled"}>Claim</button></div>` : `<p class="help">Nothing to claim on your side.</p>`}
        <div data-testid="claim-creator-status">${txLine("creator")}</div>
      </section>
    </div>
    <div class="section-title"><div><h2>Earnings per coin</h2><p>What each coin's pool has paid you so far, from ${esc(plural(perCoin.reduce((n, r) => n + (Number(r.posts) || 0), 0), "counted post"))}.</p></div></div>
    ${perCoinTable(perCoin)}
    <div class="section-title"><div><h2>Your posts</h2><p>Each post verified in a ${esc(epochName(cfg))} gets an equal share of that ${esc(epochName(cfg))}'s pool for its coin.</p></div></div>
    ${postsTable(posts)}`;

  if (S.busy === "creator") setBusy($("#claimCreator"), true, "Claiming…");
  if (S.busy === "shill") setBusy($("#claimShill"), true, "Claiming…");
  $("#claimCreator")?.addEventListener("click", () => claim("creator"));
  $("#claimShill").addEventListener("click", () => claim("shill"));
}

async function claim(kind) {
  if (S.busy || kind !== "shill") return;
  const addr = account();
  const d = S.data;
  if (!addr || !d || S.address !== addr) return;
  S.busy = kind;
  S.msg[kind] = null;
  S.tx[kind] = { status: "pending" };
  renderEarnings();
  try {
    const r = await api.claim(addr);
    const hash = r.txs[r.txs.length - 1];
    S.tx[kind] = null;
    S.msg[kind] = { kind: "ok", html: `Claimed ${esc(formatSol(r.lamports))} SOL. <a href="/paid/${esc(hash)}" data-testid="claim-receipt">Get your receipt card</a> · <a href="${esc(explorerTx(hash))}" target="_blank" rel="noopener noreferrer">View transaction</a>` };
    toast(`Claimed ${formatSol(r.lamports)} SOL.`);
    S.busy = null;
    await loadRewards(true);
  } catch (e) {
    S.tx[kind] = null;
    S.msg[kind] = { kind: "err", html: esc(friendlyError(e)) };
  } finally {
    S.busy = null;
    renderEarnings();
  }
}

async function loadRewards(force = false) {
  const addr = account();
  if (!force && addr === S.address && (S.data || S.loading)) { renderEarnings(); renderX(); return; }
  if (addr !== S.address) { S.data = null; S.msg = { creator: null, shill: null }; }
  S.address = addr;
  S.error = null;
  if (!addr) { renderEarnings(); renderX(); return; }
  const seq = ++S.seq;
  S.loading = true;
  renderEarnings();
  renderX();
  try {
    const d = await api.rewards(addr);
    if (seq !== S.seq) return;
    S.data = d || {};
  } catch (e) {
    if (seq !== S.seq) return;
    S.error = e;
  } finally {
    if (seq === S.seq) S.loading = false;
  }
  renderEarnings();
  renderX();
}

async function main() {
  renderWallet();
  renderX();
  renderEarnings();
  await boot();
  const refresh = () => { renderWallet(); loadRewards(); };
  onWallet(refresh);
  refresh();
}

main().catch((e) => console.error(e));
